import { z } from 'zod';

/*
 * Mirrors of the Postgres enums in supabase/migrations/20261102000000_initial_schema.sql.
 * enums.test.ts compares them with `Constants` in database.types.ts, so a schema
 * change without regenerated types (or without updating this file) fails the tests.
 */

export const TEAM_ROLES = ['admin', 'editor', 'verifier'] as const;
export const TeamRoleSchema = z.enum(TEAM_ROLES);
export type TeamRole = z.infer<typeof TeamRoleSchema>;

export const SOURCE_FAMILIES = ['business', 'platform', 'observation'] as const;
export const SourceFamilySchema = z.enum(SOURCE_FAMILIES);
export type SourceFamily = z.infer<typeof SourceFamilySchema>;

/**
 * State of a published fact (`public.fact_status`). "verified" requires two different
 * source families to agree; "single_source" has one; "conflicting" has a source that
 * disagrees; "expired" has no evidence inside its validity window.
 */
export const VERIFICATION_STATUSES = [
  'verified',
  'single_source',
  'conflicting',
  'expired',
] as const;
export const VerificationStatusSchema = z.enum(VERIFICATION_STATUSES);
export type VerificationStatus = z.infer<typeof VerificationStatusSchema>;

export const FACT_FIELDS = [
  'location',
  'business_status',
  'opening_hours',
  'price',
  'dish_exists',
  'ingredients',
  'allergens',
  'channel',
] as const;
export const FactFieldSchema = z.enum(FACT_FIELDS);
export type FactField = z.infer<typeof FactFieldSchema>;

export const PLACE_STATUSES = ['unconfirmed', 'active', 'temporarily_closed', 'closed'] as const;
export const PlaceStatusSchema = z.enum(PLACE_STATUSES);
export type PlaceStatus = z.infer<typeof PlaceStatusSchema>;

export const PLACE_ORIGINS = ['open_catalog', 'discovered', 'team', 'user'] as const;
export const PlaceOriginSchema = z.enum(PLACE_ORIGINS);
export type PlaceOrigin = z.infer<typeof PlaceOriginSchema>;

export const CHANNEL_TYPES = [
  'google_maps',
  'rappi',
  'menupp',
  'thefork',
  'whatsapp',
  'phone',
  'website',
  'instagram',
  'own_delivery',
  'didi_food',
] as const;
export const ChannelTypeSchema = z.enum(CHANNEL_TYPES);
export type ChannelType = z.infer<typeof ChannelTypeSchema>;

export const MENU_FORMATS = ['web', 'pdf', 'image', 'link'] as const;
export const MenuFormatSchema = z.enum(MENU_FORMATS);
export type MenuFormat = z.infer<typeof MenuFormatSchema>;

export const MENU_STATUSES = [
  'pending',
  'first_read',
  'double_read',
  'in_review',
  'published',
  'link_only',
  'expired',
] as const;
export const MenuStatusSchema = z.enum(MENU_STATUSES);
export type MenuStatus = z.infer<typeof MenuStatusSchema>;

export const MENU_READERS = ['claude', 'gemini', 'ocr', 'team'] as const;
export const MenuReaderSchema = z.enum(MENU_READERS);
export type MenuReader = z.infer<typeof MenuReaderSchema>;

export const TAG_DIMENSIONS = [
  'cuisine',
  'dish',
  'ingredient',
  'diet',
  'allergen',
  'ambience',
  'occasion',
] as const;
export const TagDimensionSchema = z.enum(TAG_DIMENSIONS);
export type TagDimension = z.infer<typeof TagDimensionSchema>;

export const TAG_ORIGINS = ['read_1', 'read_2', 'team', 'user'] as const;
export const TagOriginSchema = z.enum(TAG_ORIGINS);
export type TagOrigin = z.infer<typeof TagOriginSchema>;

export const RESTRICTION_TYPES = ['diet', 'allergy'] as const;
export const RestrictionTypeSchema = z.enum(RESTRICTION_TYPES);
export type RestrictionType = z.infer<typeof RestrictionTypeSchema>;

export const CONSENT_TYPES = ['terms', 'health_data', 'location', 'marketing'] as const;
export const ConsentTypeSchema = z.enum(CONSENT_TYPES);
export type ConsentType = z.infer<typeof ConsentTypeSchema>;

export const SPEND_OCCASIONS = ['everyday', 'going_out', 'celebration'] as const;
export const SpendOccasionSchema = z.enum(SPEND_OCCASIONS);
export type SpendOccasion = z.infer<typeof SpendOccasionSchema>;

export const MOBILITIES = ['walking', 'vehicle', 'delivery'] as const;
export const MobilitySchema = z.enum(MOBILITIES);
export type Mobility = z.infer<typeof MobilitySchema>;

export const INTERACTION_TYPES = [
  'impression',
  'opened_place',
  'opened_menu',
  'directions',
  'opened_channel',
  'saved',
  'dismissed',
  'shared',
] as const;
export const InteractionTypeSchema = z.enum(INTERACTION_TYPES);
export type InteractionType = z.infer<typeof InteractionTypeSchema>;

export const DISMISS_REASONS = [
  'too_expensive',
  'too_far',
  'not_my_style',
  'already_known',
  'other',
] as const;
export const DismissReasonSchema = z.enum(DISMISS_REASONS);
export type DismissReason = z.infer<typeof DismissReasonSchema>;

export const REPORT_TYPES = [
  'closed',
  'does_not_exist',
  'wrong_location',
  'wrong_price',
  'wrong_hours',
  'dish_missing',
  'other',
] as const;
export const ReportTypeSchema = z.enum(REPORT_TYPES);
export type ReportType = z.infer<typeof ReportTypeSchema>;

export const REPORT_STATUSES = ['new', 'confirmed', 'rejected'] as const;
export const ReportStatusSchema = z.enum(REPORT_STATUSES);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const REVIEW_REASONS = [
  'conflict',
  'expired',
  'single_source',
  'user_report',
  'allergen',
  'new_place',
  'audit',
  'status_change',
] as const;
export const ReviewReasonSchema = z.enum(REVIEW_REASONS);
export type ReviewReason = z.infer<typeof ReviewReasonSchema>;

export const REVIEW_STATUSES = ['pending', 'in_progress', 'resolved', 'dismissed'] as const;
export const ReviewStatusSchema = z.enum(REVIEW_STATUSES);
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>;

export const AUDIT_RESULTS = ['correct', 'incorrect', 'unreachable'] as const;
export const AuditResultSchema = z.enum(AUDIT_RESULTS);
export type AuditResult = z.infer<typeof AuditResultSchema>;

export const CANDIDATE_STATUSES = ['new', 'in_review', 'accepted', 'rejected'] as const;
export const CandidateStatusSchema = z.enum(CANDIDATE_STATUSES);
export type CandidateStatus = z.infer<typeof CandidateStatusSchema>;

/** Who started a data refresh (migration 20261103000100). */
export const REFRESH_TRIGGERS = ['nightly', 'manual', 'console'] as const;
export const RefreshTriggerSchema = z.enum(REFRESH_TRIGGERS);
export type RefreshTrigger = z.infer<typeof RefreshTriggerSchema>;

export const REFRESH_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export const RefreshStatusSchema = z.enum(REFRESH_STATUSES);
export type RefreshStatus = z.infer<typeof RefreshStatusSchema>;

/** A place a person suggested ("Recomiéndanos un lugar") on its way to "Por mapear". */
export const SUGGESTION_STATUSES = [
  'new',
  'matched',
  'duplicate',
  'in_review',
  'accepted',
  'rejected',
] as const;
export const SuggestionStatusSchema = z.enum(SUGGESTION_STATUSES);
export type SuggestionStatus = z.infer<typeof SuggestionStatusSchema>;

/** Role of a place in the beta list (`pilot_places`): the job drops closed ones and promotes the reserve. */
export const PILOT_ROLES = ['selected', 'reserve', 'dropped'] as const;
export const PilotRoleSchema = z.enum(PILOT_ROLES);
export type PilotRole = z.infer<typeof PilotRoleSchema>;

/** Postgres enum name → allowed values. Keys match `public` enum names in the database. */
export const DATABASE_ENUMS = {
  team_role: TEAM_ROLES,
  source_family: SOURCE_FAMILIES,
  fact_status: VERIFICATION_STATUSES,
  fact_field: FACT_FIELDS,
  place_status: PLACE_STATUSES,
  place_origin: PLACE_ORIGINS,
  channel_type: CHANNEL_TYPES,
  menu_format: MENU_FORMATS,
  menu_status: MENU_STATUSES,
  menu_reader: MENU_READERS,
  tag_dimension: TAG_DIMENSIONS,
  tag_origin: TAG_ORIGINS,
  restriction_type: RESTRICTION_TYPES,
  consent_type: CONSENT_TYPES,
  spend_occasion: SPEND_OCCASIONS,
  mobility: MOBILITIES,
  interaction_type: INTERACTION_TYPES,
  dismiss_reason: DISMISS_REASONS,
  report_type: REPORT_TYPES,
  report_status: REPORT_STATUSES,
  review_reason: REVIEW_REASONS,
  review_status: REVIEW_STATUSES,
  audit_result: AUDIT_RESULTS,
  candidate_status: CANDIDATE_STATUSES,
  refresh_trigger: REFRESH_TRIGGERS,
  refresh_status: REFRESH_STATUSES,
  suggestion_status: SUGGESTION_STATUSES,
  pilot_role: PILOT_ROLES,
} as const;
